import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  User,
  Mail,
  Briefcase,
  Image,
  Link,
  Lock,
  Info,
  GitBranch,
  FileText,
  Eye,
  Edit
} from 'lucide-react';
import StickyActionBar from '../../components/ui/StickyActionBar';
import { Card, CardContent } from '../../components/ui/Card';

interface RolePermissions {
  view: boolean;
  edit: boolean;
}

interface ProfileSection {
  label: string;
  description: string;
  icon: string;
  roles: {
    employee: RolePermissions;
    manager: RolePermissions;
    hr: RolePermissions;
    admin: RolePermissions;
  };
}

interface Permissions {
  basicInfo: ProfileSection;
  contactInfo: ProfileSection;
  professionalInfo: ProfileSection;
  photo: ProfileSection;
  socialLinks: ProfileSection;
  privacySettings: ProfileSection;
}

interface Role {
  id: string;
  label: string;
  color: string;
}

const ROLE_COLOR_MAP: Record<string, string> = {
  blue: 'bg-primary/10 text-primary',
  green: 'bg-success/10 text-success',
  purple: 'bg-accent/10 text-accent-foreground',
  red: 'bg-destructive/10 text-destructive',
};

const ProfileSettings = () => {
  const { t } = useTranslation();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Authorization settings state
  const [permissions, setPermissions] = useState<Permissions>({
    basicInfo: {
      label: 'Basic Information',
      description: 'Name, job title, department, location',
      icon: 'User',
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: true },
        hr: { view: true, edit: true },
        admin: { view: true, edit: true }
      }
    },
    contactInfo: {
      label: 'Contact Information',
      description: 'Email, phone number',
      icon: 'Mail',
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: false },
        hr: { view: true, edit: true },
        admin: { view: true, edit: true }
      }
    },
    professionalInfo: {
      label: 'Professional Information',
      description: 'Bio, skills, achievements',
      icon: 'Briefcase',
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: false },
        hr: { view: true, edit: false },
        admin: { view: true, edit: true }
      }
    },
    photo: {
      label: 'Profile Photo',
      description: 'Profile picture',
      icon: 'Image',
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: false },
        hr: { view: true, edit: true },
        admin: { view: true, edit: true }
      }
    },
    socialLinks: {
      label: 'Social Links',
      description: 'LinkedIn, GitHub, etc.',
      icon: 'Link',
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: false },
        hr: { view: true, edit: false },
        admin: { view: true, edit: true }
      }
    },
    privacySettings: {
      label: 'Privacy Settings',
      description: 'Public visibility controls',
      icon: 'Lock',
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: false },
        hr: { view: true, edit: true },
        admin: { view: true, edit: true }
      }
    }
  });

  const roles: Role[] = [
    { id: 'employee', label: 'Employee', color: 'blue' },
    { id: 'manager', label: 'Manager', color: 'green' },
    { id: 'hr', label: 'HR', color: 'purple' },
    { id: 'admin', label: 'Admin', color: 'red' }
  ];

  const handlePermissionChange = (section: keyof Permissions, role: string, permission: keyof RolePermissions, value: boolean) => {
    setPermissions(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        roles: {
          ...prev[section].roles,
          [role]: {
            ...prev[section].roles[role as keyof typeof prev[typeof section]['roles']],
            [permission]: value
          }
        }
      }
    }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);

    // Simulate API call
    setTimeout(() => {
      setIsSaving(false);
      setHasUnsavedChanges(false);
      alert(t('Authorization settings saved successfully!'));
    }, 1500);
  };

  const handleReset = () => {
    if (window.confirm(t('Are you sure you want to reset to default permissions?'))) {
      setHasUnsavedChanges(false);
      window.location?.reload();
    }
  };

  const getRoleBadgeClasses = (color: string) => {
    return ROLE_COLOR_MAP[color] || 'bg-muted text-muted-foreground';
  };

  const getIconComponent = (iconName: string) => {
    const iconMap: { [key: string]: React.ComponentType<{ className?: string }> } = {
      User,
      Mail,
      Briefcase,
      Image,
      Link,
      Lock
    };
    return iconMap[iconName] || User;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-8 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <Card variant="elevated" className="mb-6">
            <CardContent>
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-foreground">{t('Profile Authorization Settings')}</h1>
                    <p className="text-muted-foreground mt-1">{t('Control who can view and edit public profile information')}</p>
                  </div>
                </div>

              </div>
            </CardContent>
          </Card>

          {/* Info Banner */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <Info className="w-5 h-5 text-primary me-3 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-primary mb-1">{t('Authorization Guidelines')}</h3>
                <p className="text-sm text-primary">
                  {t('Configure permissions for different roles to control access to public profile sections. View permission allows reading the information, while Edit permission allows modifications.')}
                </p>
              </div>
            </div>
          </div>

          {/* Permissions Table */}
          <Card variant="elevated" className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted border-b border-border">
                  <tr>
                    <th className="px-6 py-4 text-start text-sm font-semibold text-foreground w-1/4">
                      {t('Profile Section')}
                    </th>
                    {roles.map(role => (
                      <th key={role.id} className="px-6 py-4 text-center text-sm font-semibold text-foreground">
                        <div className="flex items-center justify-center">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeClasses(role.color)}`}>
                            {t(role.label)}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {Object.entries(permissions).map(([key, section]) => {
                    const IconComponent = getIconComponent(section.icon);
                    return (
                      <tr key={key} className="hover:bg-muted/50 transition-colors duration-150">
                        <td className="px-6 py-4">
                          <div className="flex items-start">
                            <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center me-3 flex-shrink-0">
                              <IconComponent className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">{t(section.label)}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{t(section.description)}</p>
                            </div>
                          </div>
                        </td>
                        {roles.map(role => (
                          <td key={role.id} className="px-6 py-4">
                            <div className="flex items-center justify-center space-x-4 rtl:space-x-reverse">
                              {/* View Permission */}
                              <label className="flex items-center cursor-pointer group">
                                <input
                                  type="checkbox"
                                  checked={section.roles[role.id as keyof typeof section.roles]?.view}
                                  onChange={(e) => handlePermissionChange(key as keyof Permissions, role.id, 'view', e.target.checked)}
                                  className="h-4 w-4 rounded border-border text-primary focus-visible:ring-ring"
                                />
                                <span className="ms-1.5 text-xs text-muted-foreground group-hover:text-foreground">{t('View')}</span>
                              </label>

                              {/* Edit Permission */}
                              <label className="flex items-center cursor-pointer group">
                                <input
                                  type="checkbox"
                                  checked={section.roles[role.id as keyof typeof section.roles]?.edit}
                                  onChange={(e) => handlePermissionChange(key as keyof Permissions, role.id, 'edit', e.target.checked)}
                                  className="h-4 w-4 rounded border-border text-success focus-visible:ring-ring"
                                />
                                <span className="ms-1.5 text-xs text-muted-foreground group-hover:text-foreground">{t('Edit')}</span>
                              </label>
                            </div>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Additional Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">

            {/* Approval Workflow */}
            <Card variant="elevated">
              <CardContent>
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <GitBranch className="w-5 h-5 me-2" />
                  {t('Approval Workflow')}
                </h2>

                <div className="space-y-3">
                  <label className="flex items-center justify-between p-3 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-colors duration-200">
                    <div>
                      <p className="text-sm font-medium text-foreground">{t('Require manager approval')}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{t('Changes need manager approval before publishing')}</p>
                    </div>
                    <input
                      type="checkbox"
                      defaultChecked={false}
                      onChange={() => setHasUnsavedChanges(true)}
                      className="h-4 w-4 rounded border-border text-primary focus-visible:ring-ring"
                    />
                  </label>

                  <label className="flex items-center justify-between p-3 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-colors duration-200">
                    <div>
                      <p className="text-sm font-medium text-foreground">{t('Require HR approval')}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{t('Changes need HR approval before publishing')}</p>
                    </div>
                    <input
                      type="checkbox"
                      defaultChecked={true}
                      onChange={() => setHasUnsavedChanges(true)}
                      className="h-4 w-4 rounded border-border text-primary focus-visible:ring-ring"
                    />
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Audit & Logging */}
            <Card variant="elevated">
              <CardContent>
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <FileText className="w-5 h-5 me-2" />
                  {t('Audit & Logging')}
                </h2>

                <div className="space-y-3">
                  <label className="flex items-center justify-between p-3 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-colors duration-200">
                    <div>
                      <p className="text-sm font-medium text-foreground">{t('Log all changes')}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{t('Track all profile modifications')}</p>
                    </div>
                    <input
                      type="checkbox"
                      defaultChecked={true}
                      onChange={() => setHasUnsavedChanges(true)}
                      className="h-4 w-4 rounded border-border text-primary focus-visible:ring-ring"
                    />
                  </label>

                  <label className="flex items-center justify-between p-3 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-colors duration-200">
                    <div>
                      <p className="text-sm font-medium text-foreground">{t('Notify on changes')}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{t('Send notifications when profile is updated')}</p>
                    </div>
                    <input
                      type="checkbox"
                      defaultChecked={false}
                      onChange={() => setHasUnsavedChanges(true)}
                      className="h-4 w-4 rounded border-border text-primary focus-visible:ring-ring"
                    />
                  </label>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Legend */}
          <div className="bg-muted rounded-lg p-4 mt-6">
            <h3 className="text-sm font-semibold text-foreground mb-3">{t('Permission Legend')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-start">
                <Eye className="w-4 h-4 text-primary me-2 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">{t('View Permission')}</p>
                  <p className="text-xs text-muted-foreground">{t('Allows viewing the profile section information')}</p>
                </div>
              </div>
              <div className="flex items-start">
                <Edit className="w-4 h-4 text-success me-2 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">{t('Edit Permission')}</p>
                  <p className="text-xs text-muted-foreground">{t('Allows modifying the profile section information')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sticky Action Bar */}
          <StickyActionBar
            hasUnsavedChanges={hasUnsavedChanges}
            isSaving={isSaving}
            onReset={handleReset}
            onSave={handleSave}
            t={t}
            resetLabel={t('Reset to Default')}
          />
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
